import { useEffect, useState } from "react";
import Marquee from "react-fast-marquee";
import { useTranslation } from "@thyra/landing-i18n";

type Connection = {
  src: string;
  alt: string;
  label?: string;
  labelKey?: "logoCloud.calendarLabel";
};

const CONNECTIONS: Connection[] = [
  { src: "/connections/gmail.svg", alt: "Gmail", label: "Gmail" },
  {
    src: "/connections/googlecalendar.svg",
    alt: "Google Calendar",
    labelKey: "logoCloud.calendarLabel",
  },
  { src: "/connections/linear.svg", alt: "Linear", label: "Linear" },
  { src: "/connections/calcom.svg", alt: "Cal.com", label: "Cal.com" },
  { src: "/connections/twilio.svg", alt: "Twilio", label: "Twilio" },
  { src: "/connections/outlook.svg", alt: "Outlook", label: "Outlook" },
  { src: "/connections/slack.svg", alt: "Slack", label: "Slack" },
];

const MARQUEE_PROPS = {
  autoFill: true,
  gradient: true,
  gradientColor: "var(--color-background)",
  gradientWidth: 40,
  pauseOnHover: true,
} as const;

function LogoPill({ src, alt, label, labelKey }: Connection) {
  const { t } = useTranslation("home");

  return (
    <div
      className="inline-flex h-9 shrink-0 items-center gap-2 rounded-full border border-border/70 bg-background px-1 shadow-sm"
      title={alt}
    >
      <div className="flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted/60 p-1.5">
        <img
          alt=""
          src={src}
          aria-hidden
          className="block size-full object-contain object-center"
        />
      </div>
      <span className="whitespace-nowrap pr-2.5 text-sm font-medium text-foreground">
        {labelKey ? t(labelKey) : label}
      </span>
    </div>
  );
}

function IntegrationStrip({
  connections,
  speed,
  direction = "left",
  className,
  play = true,
}: {
  connections: typeof CONNECTIONS;
  speed: number;
  direction?: "left" | "right";
  className?: string;
  play?: boolean;
}) {
  return (
    <Marquee
      {...MARQUEE_PROPS}
      speed={speed}
      direction={direction}
      play={play}
      className={className}
    >
      <div className="flex items-center gap-2.5 pr-2.5">
        {connections.map((conn) => (
          <LogoPill key={conn.alt} {...conn} />
        ))}
      </div>
    </Marquee>
  );
}

function IntegrationMarquee({ play = true }: { play?: boolean }) {
  const reversed = [...CONNECTIONS].reverse();

  return (
    <div className="relative -mx-1 flex flex-col gap-2.5 overflow-hidden sm:-mx-2">
      <IntegrationStrip
        connections={CONNECTIONS}
        speed={26}
        direction="left"
        className="overflow-hidden py-0.5"
        play={play}
      />
      <IntegrationStrip
        connections={reversed}
        speed={32}
        direction="right"
        className="overflow-hidden py-0.5 pl-6"
        play={play}
      />
    </div>
  );
}

function IntegrationGrid() {
  return (
    <div className="flex flex-wrap justify-center gap-2.5">
      {CONNECTIONS.map((conn) => (
        <LogoPill key={conn.alt} {...conn} />
      ))}
    </div>
  );
}

export function LogoCloud() {
  const { t } = useTranslation("home");
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduceMotion(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return (
    <section className="w-full overflow-hidden py-10 sm:py-12">
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-3xl border border-border/80 bg-background p-5 shadow-sm sm:p-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between lg:gap-10">
            <div className="max-w-md shrink-0">
              <h3 className="text-lg font-semibold tracking-tight text-foreground">
                {t("logoCloud.title")}
              </h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                {t("logoCloud.description")}
              </p>
            </div>

            <div className="min-w-0 flex-1 lg:max-w-xl">
              {reduceMotion ? <IntegrationGrid /> : <IntegrationMarquee play />}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
